import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useOrganizations } from '@/hooks/useOrganizations';
import { FEATURE_GATES, FeatureKey } from '@/lib/featureGates';
import { useDialog } from '@/providers/DialogProvider';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface UpgradeDialogProps {
  feature: FeatureKey;
}

export const UpgradeDialog = ({ feature }: UpgradeDialogProps) => {
  const { t } = useTranslation('featureGate');
  const { dismissActiveDialog } = useDialog();
  const { hasRole } = useOrganizations();
  const navigate = useNavigate();

  const gate = FEATURE_GATES[feature];
  const featureName = t(`feature.${feature}`);
  const canUpgrade = hasRole('BILLING_MANAGER');

  // Derive a human-readable requirement label from the gate
  const requirementKey = gate.minProduct === 'PRO'
    ? 'requirement.pro'
    : gate.minStorageBytes != null && gate.minStorageBytes >= 1_000_000_000_000
      ? 'requirement.business'
      : gate.minStorageBytes != null && gate.minStorageBytes >= 500_000_000_000
        ? 'requirement.team'
        : null;
  const requirement = requirementKey ? t(requirementKey) : null;

  const handleViewPlans = () => {
    dismissActiveDialog();
    navigate('/subscription');
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('dialog.title', { feature: featureName })}</DialogTitle>
        <DialogDescription>
          {requirement
            ? canUpgrade
              ? t('dialog.body', { feature: featureName, requirement })
              : t('dialog.askAdmin', { feature: featureName, requirement })
            : canUpgrade
              ? t('dialog.bodyNoRequirement', { feature: featureName })
              : t('dialog.askAdminNoRequirement', { feature: featureName })}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant='outline' onClick={dismissActiveDialog}>
          {t('dialog.cancel')}
        </Button>
        {canUpgrade && <Button onClick={handleViewPlans}>{t('dialog.cta')}</Button>}
      </DialogFooter>
    </>
  );
};
